<template>
  <v-tooltip location="bottom" max-width="440" :open-delay="150" content-class="gauge-term-tooltip">
    <template #activator="{ props: activatorProps }">
      <span v-bind="activatorProps" class="gauge-term" tabindex="0">{{ label }}</span>
    </template>

    <div class="gauge-term-tooltip__title">{{ label }}{{ gaugeDescription ? ` — ${gaugeDescription}` : "" }}</div>
    <div v-if="!explanation.rows.length">Aucune donnée pour cette station.</div>
    <table v-else class="gauge-term-tooltip__table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Heure</th>
          <th class="text-right">Débit</th>
        </tr>
      </thead>
      <tbody>
        <template v-for="(row, index) in explanation.rows" :key="index">
          <tr v-if="row.separator">
            <td colspan="3" class="text-center">…</td>
          </tr>
          <tr v-else :class="{ 'gauge-term-tooltip__used': row.used }">
            <td>{{ row.date }}</td>
            <td>{{ row.hour }}</td>
            <td class="text-right">
              {{ formatFlow(row.flow) }}
              <span v-if="row.used" aria-label="donnée utilisée">◀</span>
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <div v-if="explanation.value !== null" class="gauge-term-tooltip__summary">
      <div v-if="lagHours > 0">Donnée d'il y a {{ formatNumber(lagHours) }} h par rapport au dernier calcul.</div>
      <div>
        Valeur utilisée : <strong>{{ formatFlow(explanation.value) }}</strong>
        <span v-if="explanation.interpolated"> (entre les deux lignes marquées)</span>
      </div>
      <div>
        Tendance sur 1 h : {{ explanation.trend >= 0 ? "+" : "" }}{{ formatNumber(explanation.trend) }} →
        {{ explanation.side === "up" ? "facteur montant" : "facteur descendant" }}
        {{ formatNumber(explanation.factor) }}
      </div>
      <div>
        Part dans le calcul : {{ operator === "-" ? "−" : "+" }} {{ formatNumber(explanation.factor) }} ×
        {{ formatNumber(explanation.value) }} = <strong>{{ formatFlow(explanation.contribution) }}</strong>
      </div>
    </div>
    <div v-else-if="explanation.rows.length" class="gauge-term-tooltip__summary">
      Aucune donnée de cette station au moment du dernier calcul.
    </div>
  </v-tooltip>
</template>

<script>
  // Station number in the formula of the Station tab: on hover, its latest readings with the ones used by the
  // latest value of the formula marked, and how they turned into its part of the result (utils/flowFormulas.js).
  export default {
    name: "GaugeTermTooltip",
    props: {
      label: {
        type: String,
        required: true,
      },
      gaugeDescription: {
        type: String,
        default: "",
      },
      operator: {
        type: String,
        default: "+",
      },
      lagHours: {
        type: Number,
        default: 0,
      },
      // explainTermValue()
      explanation: {
        type: Object,
        required: true,
      },
    },
    methods: {
      formatNumber(value) {
        return Number.isFinite(Number(value))
          ? (Math.round(Number(value) * 1000) / 1000).toLocaleString("fr-CA", { maximumFractionDigits: 3 })
          : "—";
      },
      formatFlow(value) {
        return Number.isFinite(Number(value))
          ? `${Number(value).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} mcs`
          : "—";
      },
    },
  };
</script>

<style scoped>
  .gauge-term {
    text-decoration: underline dotted;
    text-underline-offset: 3px;
    cursor: help;
    font-weight: 700;
  }

  .gauge-term:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
</style>

<style>
  /* The tooltip is rendered outside the component (overlay), so these styles are not scoped */
  .gauge-term-tooltip__title {
    font-weight: 700;
    margin-bottom: 4px;
  }

  .gauge-term-tooltip__table {
    border-collapse: collapse;
    font-size: 0.8rem;
    font-variant-numeric: tabular-nums;
  }

  .gauge-term-tooltip__table th,
  .gauge-term-tooltip__table td {
    padding: 1px 8px;
  }

  .gauge-term-tooltip__table th {
    font-weight: 600;
    opacity: 0.8;
  }

  /* Readings used by the latest value: gold, the brand's "now" accent */
  .gauge-term-tooltip__used td {
    background: rgba(224, 165, 38, 0.35);
    font-weight: 700;
  }

  .gauge-term-tooltip__summary {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid rgba(255, 255, 255, 0.3);
    font-size: 0.8rem;
  }
</style>
